/**
 * PeerManager - Gestionnaire de connexions WebRTC P2P
 * Inspiré de Video-Peers
 */
class PeerManager {
  constructor() {
    this.peers = new Map(); // remoteId -> RTCPeerConnection
    this.iceServers = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:global.stun.twilio.com:3478' }
      ]
    };
    this.onTrack = null;
    this.onIceCandidate = null;
    this.pendingCandidates = new Map(); // remoteId -> candidate[]
  }

  /**
   * Obtenir toutes les connexions peer
   */
  getAllPeers() {
    return this.peers;
  }

  /**
   * Créer une nouvelle connexion peer
   */
  createPeer(remoteId, onTrack, onIceCandidate, onConnectionFailed = null) {
    if (this.peers.has(remoteId)) {
      return this.peers.get(remoteId);
    }

    const peer = new RTCPeerConnection(this.iceServers);

    // Gérer les tracks reçus
    peer.ontrack = (event) => {
      if (onTrack && event.streams && event.streams[0]) {
        onTrack(remoteId, event.streams[0]);
      }
    };

    // Gérer les ICE candidates
    peer.onicecandidate = (event) => {
      if (event.candidate && onIceCandidate) {
        onIceCandidate(remoteId, event.candidate);
      }
    };

    // Gérer les changements d'état de connexion avec diagnostics détaillés
    peer.onconnectionstatechange = () => {
      const state = peer.connectionState;
      const iceState = peer.iceConnectionState;
      const signalingState = peer.signalingState;
      
      console.log(`[PeerManager] 🔄 Connection state changed for ${remoteId}:`, {
        connectionState: state,
        iceConnectionState: iceState,
        signalingState: signalingState,
        localDescription: peer.localDescription ? 'set' : 'not set',
        remoteDescription: peer.remoteDescription ? 'set' : 'not set',
        senders: peer.getSenders().length,
        receivers: peer.getReceivers().length
      });
      
      if (state === 'failed') {
        // Vérifier les tracks et senders/receivers
        const senders = peer.getSenders();
        const receivers = peer.getReceivers();
        const senderTracks = senders.map(s => ({
          kind: s.track?.kind,
          id: s.track?.id,
          enabled: s.track?.enabled,
          readyState: s.track?.readyState,
          muted: s.track?.muted
        })).filter(Boolean);
        const receiverTracks = receivers.map(r => ({
          kind: r.track?.kind,
          id: r.track?.id,
          enabled: r.track?.enabled,
          readyState: r.track?.readyState,
          muted: r.track?.muted
        })).filter(Boolean);
        
        const possibleCauses = [
          iceState === 'failed' ? 'ICE negotiation failed' : null,
          iceState === 'disconnected' ? 'ICE disconnected' : null,
          signalingState === 'closed' ? 'Signaling closed' : null,
          !peer.localDescription ? 'No local description' : null,
          !peer.remoteDescription ? 'No remote description' : null,
          senderTracks.length === 0 ? 'No sender tracks' : null,
          receiverTracks.length === 0 ? 'No receiver tracks' : null,
          senderTracks.some(t => t.readyState !== 'live') ? 'Some sender tracks not live' : null,
          receiverTracks.some(t => t.readyState !== 'live') ? 'Some receiver tracks not live' : null,
          // Vérifier si c'est un problème de timing (ICE connecté mais connexion échoue)
          iceState === 'connected' && state === 'failed' ? 'ICE connected but connection failed - possible network issue, track problem, or connection timeout' : null,
          // Vérifier si les tracks sont mutés
          senderTracks.some(t => t.muted) ? 'Some sender tracks are muted' : null,
          receiverTracks.some(t => t.muted) ? 'Some receiver tracks are muted' : null
        ].filter(Boolean);
        
        // Si ICE est connecté mais la connexion échoue, c'est souvent un problème de tracks
        if (iceState === 'connected' && state === 'failed') {
          console.warn(`[PeerManager] ⚠️ ICE is connected but connection failed - this often indicates a track issue`);
          console.warn(`[PeerManager] 📊 Track status:`, {
            senders: senders.length,
            receivers: receivers.length,
            activeSenderTracks: senderTracks.filter(t => t.readyState === 'live').length,
            activeReceiverTracks: receiverTracks.filter(t => t.readyState === 'live').length,
            mutedSenderTracks: senderTracks.filter(t => t.muted).length,
            mutedReceiverTracks: receiverTracks.filter(t => t.muted).length
          });
        }
        
        console.error(`[PeerManager] ❌ Connection FAILED with ${remoteId}`, {
          iceConnectionState: iceState,
          signalingState: signalingState,
          localDescription: peer.localDescription?.sdp?.substring(0, 100),
          remoteDescription: peer.remoteDescription?.sdp?.substring(0, 100),
          sendersCount: senders.length,
          receiversCount: receivers.length,
          senderTracks: senderTracks,
          receiverTracks: receiverTracks,
          possibleCauses: possibleCauses,
          // Informations supplémentaires pour diagnostic
          localDescriptionType: peer.localDescription?.type,
          remoteDescriptionType: peer.remoteDescription?.type,
          // Vérifier les statistiques de connexion si disponibles
          connectionStateHistory: 'Check browser network tab for connection issues'
        });
        
        // Appeler le callback si fourni
        if (onConnectionFailed) {
          onConnectionFailed(remoteId);
        }
      } else if (state === 'disconnected') {
        console.warn(`[PeerManager] ⚠️ Connection DISCONNECTED with ${remoteId}`, {
          iceConnectionState: iceState,
          signalingState: signalingState
        });
      } else if (state === 'connected') {
        console.log(`[PeerManager] ✅ Connection CONNECTED with ${remoteId}`);
      }
    };

    peer.oniceconnectionstatechange = () => {
      const iceState = peer.iceConnectionState;
      console.log(`[PeerManager] 🧊 ICE connection state with ${remoteId}:`, iceState);
      
      if (iceState === 'failed') {
        console.error(`[PeerManager] ❌ ICE FAILED with ${remoteId}`, {
          connectionState: peer.connectionState,
          signalingState: peer.signalingState,
          localDescription: peer.localDescription ? 'set' : 'not set',
          remoteDescription: peer.remoteDescription ? 'set' : 'not set',
          localCandidates: 'Check ICE gathering state',
          remoteCandidates: 'Check if candidates were received'
        });
        
        // ICE failed - peut-être essayer de redémarrer la négociation ICE
        // Note: WebRTC ne permet pas de redémarrer directement, il faut recréer la connexion
        console.warn(`[PeerManager] 💡 Suggestion: May need to recreate peer connection for ${remoteId}`);
      } else if (iceState === 'disconnected') {
        console.warn(`[PeerManager] ⚠️ ICE DISCONNECTED with ${remoteId}`);
      } else if (iceState === 'connected') {
        console.log(`[PeerManager] ✅ ICE CONNECTED with ${remoteId}`);
      } else if (iceState === 'checking') {
        console.log(`[PeerManager] 🔍 ICE CHECKING with ${remoteId} - gathering candidates...`);
      } else if (iceState === 'completed') {
        console.log(`[PeerManager] ✅ ICE COMPLETED with ${remoteId}`);
      }
    };

    // Gérer les erreurs ICE
    peer.onicegatheringstatechange = () => {
      console.log(`[PeerManager] 🧊 ICE gathering state with ${remoteId}:`, peer.iceGatheringState);
    };

    // Gérer les erreurs de signalisation
    peer.onsignalingstatechange = () => {
      console.log(`[PeerManager] 📡 Signaling state with ${remoteId}:`, peer.signalingState);
      if (peer.signalingState === 'closed') {
        console.warn(`[PeerManager] ⚠️ Signaling CLOSED with ${remoteId}`);
      }
    };

    this.peers.set(remoteId, peer);
    this.pendingCandidates.set(remoteId, []);
    return peer;
  }

  /**
   * Ajouter les candidats ICE en attente
   */
  flushPendingCandidates(remoteId) {
    const peer = this.peers.get(remoteId);
    if (!peer) return;

    const queue = this.pendingCandidates.get(remoteId) || [];
    while (queue.length) {
      const candidate = queue.shift();
      peer.addIceCandidate(new RTCIceCandidate(candidate)).catch((err) => {
        console.warn('[PeerManager] Error adding pending ICE candidate:', err);
      });
    }
  }

  /**
   * Ajouter des tracks média à une connexion peer
   */
  addTracks(remoteId, stream) {
    const peer = this.peers.get(remoteId);
    if (!peer || !stream) {
      console.warn('[PeerManager] ⚠️ Cannot add tracks:', { hasPeer: !!peer, hasStream: !!stream, remoteId });
      return;
    }

    // Vérifier si les tracks sont déjà ajoutés pour éviter les doublons
    const existingSenders = peer.getSenders();
    const existingTrackIds = new Set(existingSenders.map(s => s.track?.id).filter(Boolean));
    
    stream.getTracks().forEach((track) => {
      // Ne pas ajouter si le track existe déjà
      if (existingTrackIds.has(track.id)) {
        console.log('[PeerManager] Track already added:', track.kind, track.id);
        return;
      }
      
      try {
        peer.addTrack(track, stream);
        console.log('[PeerManager] ✅ Added track:', track.kind, track.id, 'to peer:', remoteId);
      } catch (error) {
        console.error('[PeerManager] ❌ Error adding track:', error);
      }
    });
    
    // Vérifier les senders après ajout
    const sendersAfter = peer.getSenders();
    console.log('[PeerManager] Senders after addTracks:', sendersAfter.map(s => ({ kind: s.track?.kind, id: s.track?.id })));
  }

  /**
   * Créer une offre WebRTC
   */
  async createOffer(remoteId) {
    const peer = this.peers.get(remoteId);
    if (!peer) {
      console.warn('[PeerManager] ⚠️ Cannot create offer: peer not found for', remoteId);
      return null;
    }

    // Vérifier l'état de la connexion
    if (peer.signalingState === 'closed') {
      console.warn('[PeerManager] ⚠️ Cannot create offer: connection is closed');
      return null;
    }

    try {
      // On ne peut créer une offre que dans les états suivants :
      // - 'stable' : connexion établie, on peut créer une nouvelle offre pour re-négocier
      // - 'have-remote-offer' : on a reçu une offre, on doit créer une réponse, pas une offre
      // - 'have-local-offer' : on a déjà une offre en attente, on ne peut pas en créer une nouvelle
      
      if (peer.signalingState === 'have-remote-offer') {
        console.warn('[PeerManager] ⚠️ Cannot create offer: have remote offer, need to create answer first. Current state:', peer.signalingState);
        return null;
      }
      
      if (peer.signalingState === 'have-local-offer') {
        console.warn('[PeerManager] ⚠️ Already have local offer, cannot create new offer. Current state:', peer.signalingState);
        return null;
      }

      // Seulement créer une offre si on est en état 'stable' ou initial
      if (peer.signalingState !== 'stable' && peer.signalingState !== 'have-local-pranswer' && peer.signalingState !== 'have-remote-pranswer') {
        console.warn('[PeerManager] ⚠️ Cannot create offer in current state:', peer.signalingState);
        return null;
      }

      const senders = peer.getSenders();
      const hasVideo = senders.some(s => s.track?.kind === 'video');
      const hasAudio = senders.some(s => s.track?.kind === 'audio');
      
      console.log('[PeerManager] 📤 Creating offer for:', remoteId, 'State:', peer.signalingState, 'Has video:', hasVideo, 'Has audio:', hasAudio);
      
      const offer = await peer.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      await peer.setLocalDescription(offer);
      console.log('[PeerManager] ✅ Offer created and set as local description');
      return offer;
    } catch (error) {
      console.error('[PeerManager] ❌ Error creating offer:', error, 'Current state:', peer.signalingState);
      return null;
    }
  }

  /**
   * Créer une réponse WebRTC
   */
  async createAnswer(remoteId, offer) {
    const peer = this.peers.get(remoteId);
    if (!peer) {
      console.warn('[PeerManager] ⚠️ Cannot create answer: peer not found for', remoteId);
      return null;
    }

    if (peer.signalingState === 'closed') {
      console.warn('[PeerManager] ⚠️ Cannot create answer: connection is closed');
      return null;
    }
    
    // Si on a déjà une description distante et qu'on est en état stable,
    // on doit d'abord créer une nouvelle offre pour re-négocier
    if (peer.signalingState === 'stable' && peer.remoteDescription) {
      console.log('[PeerManager] 📊 Connection is stable, need to create new offer for renegotiation first');
      // Ne pas traiter comme une réponse, retourner null pour forcer une nouvelle offre
      return null;
    }

    // Si on est déjà en état have-remote-offer, on ne peut pas créer une nouvelle réponse
    // sans avoir d'abord résolu l'offre précédente
    if (peer.signalingState === 'have-remote-offer' && peer.remoteDescription) {
      // Vérifier si c'est la même offre ou une nouvelle
      const existingSdp = peer.remoteDescription.sdp;
      const newSdp = offer.sdp;
      
      if (existingSdp === newSdp) {
        console.log('[PeerManager] ℹ️ Same offer received, already processing');
        return null;
      } else {
        console.warn('[PeerManager] ⚠️ New offer received while already have remote offer, resetting connection');
        // Fermer et recréer la connexion pour éviter les conflits
        peer.close();
        this.peers.delete(remoteId);
        this.pendingCandidates.delete(remoteId);
        return null;
      }
    }

    try {
      console.log('[PeerManager] 📥 Setting remote description (offer) for:', remoteId, 'Current state:', peer.signalingState);
      
      // Utiliser setRemoteDescription avec gestion d'erreur améliorée
      try {
        await peer.setRemoteDescription(new RTCSessionDescription(offer));
      } catch (setRemoteError) {
        // Si l'erreur est due à un état invalide, essayer de réinitialiser
        if (setRemoteError.name === 'InvalidStateError' || setRemoteError.name === 'InvalidAccessError') {
          console.warn('[PeerManager] ⚠️ Error setting remote description, current state:', peer.signalingState, 'Error:', setRemoteError.name);
          
          // Si on est en état have-local-offer, on a un conflit - les deux pairs ont créé une offre
          if (peer.signalingState === 'have-local-offer') {
            console.log('[PeerManager] 🔄 Both peers created offers (glare), rolling back to create answer');
            // Annuler l'offre locale et créer une réponse à la place
            try {
              await peer.setLocalDescription({ type: 'rollback' });
            } catch (rollbackError) {
              console.warn('[PeerManager] ⚠️ Rollback not supported, closing and recreating connection');
              peer.close();
              this.peers.delete(remoteId);
              this.pendingCandidates.delete(remoteId);
              return null;
            }
            // Réessayer de définir la description distante
            await peer.setRemoteDescription(new RTCSessionDescription(offer));
          } else {
            throw setRemoteError;
          }
        } else {
          throw setRemoteError;
        }
      }
      
      console.log('[PeerManager] 📤 Creating answer for:', remoteId);
      const answer = await peer.createAnswer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      await peer.setLocalDescription(answer);
      console.log('[PeerManager] ✅ Answer created and set as local description, new state:', peer.signalingState);
      
      this.flushPendingCandidates(remoteId);
      return answer;
    } catch (error) {
      console.error('[PeerManager] ❌ Error creating answer:', error.name, error.message, 'Current state:', peer.signalingState);
      return null;
    }
  }

  /**
   * Définir la réponse distante
   */
  async setRemoteAnswer(remoteId, answer) {
    const peer = this.peers.get(remoteId);
    if (!peer) {
      console.warn('[PeerManager] ⚠️ Cannot set remote answer: peer not found for', remoteId);
      return;
    }

    if (peer.signalingState === 'closed') {
      console.warn('[PeerManager] ⚠️ Cannot set remote answer: connection is closed');
      return;
    }
    
    // Vérifier si une réponse a déjà été définie
    if (peer.remoteDescription && peer.signalingState === 'stable') {
      // Comparer les SDP pour voir si c'est la même réponse ou une nouvelle
      const existingSdp = peer.remoteDescription.sdp;
      const newSdp = answer.sdp;
      
      if (existingSdp === newSdp) {
        console.log('[PeerManager] ✅ Remote answer already set (same SDP), ignoring duplicate');
        return;
      } else {
        console.warn('[PeerManager] ⚠️ Remote answer already set but SDP differs. Current state:', peer.signalingState);
        console.warn('[PeerManager] ⚠️ Connection is already stable, ignoring new answer');
        // Ne pas définir une nouvelle réponse si on est déjà stable avec une réponse
        // La connexion est déjà établie, pas besoin de changer
        return;
      }
    }
    
    // Si la connexion est disconnected ou failed, ne pas essayer de définir une réponse
    if (peer.connectionState === 'disconnected' || peer.connectionState === 'failed' || 
        peer.iceConnectionState === 'disconnected' || peer.iceConnectionState === 'failed') {
      console.warn('[PeerManager] ⚠️ Cannot set remote answer: connection is disconnected/failed');
      console.warn('[PeerManager] ⚠️ Connection state:', peer.connectionState, 'ICE state:', peer.iceConnectionState);
      return;
    }
    
    // Seulement définir si on est en état have-local-offer
    // Si on est en état have-remote-offer, cela signifie qu'on a reçu une offre
    // et qu'on devrait créer une réponse, pas définir une réponse distante
    if (peer.signalingState === 'have-remote-offer') {
      console.warn('[PeerManager] ⚠️ Cannot set remote answer: in have-remote-offer state');
      console.warn('[PeerManager] ⚠️ This means we received an offer and should create an answer, not set a remote answer');
      console.warn('[PeerManager] ⚠️ This is likely a glare condition - both peers created offers');
      console.warn('[PeerManager] ⚠️ Ignoring this answer and will handle the received offer instead');
      return;
    }
    
    if (peer.signalingState !== 'have-local-offer') {
      console.warn('[PeerManager] ⚠️ Cannot set remote answer: invalid signaling state', peer.signalingState, 'Expected: have-local-offer');
      return;
    }

    try {
      console.log('[PeerManager] 📥 Setting remote description (answer) for:', remoteId, 'Current state:', peer.signalingState);
      
      // Vérification finale de l'état juste avant l'appel (pour éviter les conditions de course)
      if (peer.signalingState === 'have-remote-offer') {
        console.warn('[PeerManager] ⚠️ State changed to have-remote-offer before setRemoteDescription');
        console.warn('[PeerManager] ⚠️ Cannot set remote answer in this state - ignoring');
        return;
      }
      
      if (peer.signalingState !== 'have-local-offer') {
        console.warn('[PeerManager] ⚠️ Invalid state for setRemoteAnswer:', peer.signalingState);
        return;
      }
      
      // Vérifier le rôle DTLS dans la réponse pour éviter les conflits
      const answerSdp = answer.sdp || '';
      const isDtlsClient = answerSdp.includes('a=setup:active') || answerSdp.includes('a=fingerprint');
      
      await peer.setRemoteDescription(new RTCSessionDescription(answer));
      console.log('[PeerManager] ✅ Remote answer set successfully, new state:', peer.signalingState);
      this.flushPendingCandidates(remoteId);
    } catch (error) {
      // Vérifier l'état actuel après l'erreur (il peut avoir changé)
      const currentState = peer.signalingState;
      
      // Si l'erreur est due à have-remote-offer, ignorer silencieusement
      if (error.name === 'InvalidStateError' && 
          (error.message.includes('have-remote-offer') || currentState === 'have-remote-offer')) {
        console.warn('[PeerManager] ⚠️ Cannot set remote answer: in have-remote-offer state (glare condition)');
        console.warn('[PeerManager] ⚠️ Ignoring this answer - will handle received offer instead');
        return; // Ne pas throw, juste ignorer
      }
      // Si l'erreur est InvalidStateError et qu'on est déjà stable, c'est normal (réponse déjà définie)
      if (error.name === 'InvalidStateError' && peer.signalingState === 'stable') {
        console.log('[PeerManager] ℹ️ Answer already set (connection is stable), ignoring InvalidStateError');
        return;
      }
      
      // Gérer les erreurs InvalidAccessError liées au rôle SSL/DTLS
      if (error.name === 'InvalidAccessError' && error.message.includes('SSL role')) {
        console.warn('[PeerManager] ⚠️ SSL role conflict detected, this may indicate a glare condition');
        console.warn('[PeerManager] ⚠️ Current state:', peer.signalingState, 'Local desc:', peer.localDescription?.type, 'Remote desc:', peer.remoteDescription?.type);
        
        // Si on est en état have-local-offer, on peut essayer de rollback
        if (peer.signalingState === 'have-local-offer') {
          try {
            console.log('[PeerManager] 🔄 Attempting rollback to resolve SSL role conflict');
            await peer.setLocalDescription({ type: 'rollback' });
            // Réessayer après rollback
            await peer.setRemoteDescription(new RTCSessionDescription(answer));
            console.log('[PeerManager] ✅ Remote answer set after rollback');
            this.flushPendingCandidates(remoteId);
            return;
          } catch (rollbackError) {
            console.error('[PeerManager] ❌ Rollback failed:', rollbackError.name, rollbackError.message);
          }
        }
      }
      
      // Si l'erreur est InvalidStateError avec have-remote-offer, ne pas throw
      if (error.name === 'InvalidStateError' && 
          (error.message.includes('have-remote-offer') || currentState === 'have-remote-offer')) {
        console.warn('[PeerManager] ⚠️ InvalidStateError with have-remote-offer - ignoring answer');
        return; // Ne pas throw, juste ignorer
      }
      
      console.error('[PeerManager] Error setting remote answer:', error.name, error.message, 'Current state:', currentState);
      throw error; // Re-throw pour que l'appelant puisse gérer
    }
  }

  /**
   * Ajouter un candidat ICE
   */
  async addIceCandidate(remoteId, candidate) {
    const peer = this.peers.get(remoteId);
    if (!peer || !candidate) return;

    // Si la description distante n'est pas encore définie, mettre en file d'attente
    if (!peer.remoteDescription) {
      const queue = this.pendingCandidates.get(remoteId) || [];
      queue.push(candidate);
      this.pendingCandidates.set(remoteId, queue);
      return;
    }

    try {
      await peer.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      console.warn('[PeerManager] Error adding ICE candidate:', error);
    }
  }

  /**
   * Activer/Désactiver l'audio
   */
  toggleAudio() {
    let newState = null;
    this.peers.forEach((peer) => {
      const sender = peer.getSenders().find((s) => s.track && s.track.kind === 'audio');
      if (sender?.track) {
        sender.track.enabled = !sender.track.enabled;
        if (newState === null) {
          newState = sender.track.enabled;
        }
      }
    });
    return newState;
  }

  /**
   * Activer/Désactiver la vidéo
   */
  toggleVideo() {
    let newState = null;
    this.peers.forEach((peer) => {
      const sender = peer.getSenders().find((s) => s.track && s.track.kind === 'video');
      if (sender?.track) {
        sender.track.enabled = !sender.track.enabled;
        if (newState === null) {
          newState = sender.track.enabled;
        }
      }
    });
    return newState;
  }

  /**
   * Fermer une connexion peer spécifique
   */
  closePeer(remoteId) {
    const peer = this.peers.get(remoteId);
    if (peer) {
      peer.close();
      this.peers.delete(remoteId);
      this.pendingCandidates.delete(remoteId);
    }
  }

  /**
   * Fermer toutes les connexions
   */
  closeAll() {
    this.peers.forEach((peer) => peer.close());
    this.peers.clear();
    this.pendingCandidates.clear();
  }

  /**
   * Obtenir le nombre de connexions actives
   */
  getPeerCount() {
    return this.peers.size;
  }

  /**
   * Vérifier si une connexion existe
   */
  hasPeer(remoteId) {
    return this.peers.has(remoteId);
  }
}

// Export singleton
export default new PeerManager();

